/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { omit } from 'lodash';

import useRouter, { useLocation } from 'js/lib/useRouter';

import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import SearchBarContent from 'bundles/search-common/components/searchbar/SearchBarContent';
import { AUTOCOMPLETE_SECTION_PREFIXES, autocompletePrefixes } from 'bundles/search-common/constants/autocomplete';
import type { AutocompleteMode } from 'bundles/search-common/constants/autocomplete';
import { useAutocompleteSearchConfigs } from 'bundles/search-common/hooks/useAutocompleteSearchConfigs';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { saveRecentlySearched } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItems,
  getRecentlySearchedAutocompleteItems,
  getRecentlyViewedAutocompleteItems,
  getSearchUpdatedUrl,
  inferAutocompleteMode,
  isExactPartnerNameMatch,
} from 'bundles/search-common/utils/autocomplete';
import { shouldAllowSearchNavigation } from 'bundles/search-common/utils/emptyQueryValidation';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useTrackSearch from 'bundles/search-common/utils/useTrackSearch';

export type Props = {
  searchIsOpen?: boolean;
  hideMobileSearchPage?: () => void;
  collectionRecommendations?: DiscoveryCollection | null;
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    width: 100%;
  `,

  /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
  searchForm: css`
    margin-bottom: 0;
    width: 100%;
  `,
};

const SearchBarContentWrapper = (props: Props) => {
  const { hideMobileSearchPage, searchIsOpen } = props;
  const router = useRouter();
  const currentQueryParams = router.location?.query;
  const isConsumerSearchPage = useIsConsumerSerp();
  const [searchText, setSearchText] = useState<string>(currentQueryParams?.query || '');
  const [refreshKey, setRefreshKey] = useState(0);

  // Recompute items when refreshKey changes to pick up localStorage updates
  const recentlySearchedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = refreshKey;
    return getRecentlySearchedAutocompleteItems();
  }, [refreshKey]);

  const recentlyViewedItems = React.useMemo(() => {
    // refreshKey is intentionally used to force recomputation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = refreshKey;
    return getRecentlyViewedAutocompleteItems(props.collectionRecommendations);
  }, [refreshKey, props.collectionRecommendations]);

  const [autocompleteMode, setAutocompleteMode] = useState<AutocompleteMode>(() =>
    inferAutocompleteMode(
      searchText,
      getRecentlySearchedAutocompleteItems(),
      getRecentlyViewedAutocompleteItems(props.collectionRecommendations)
    )
  );
  const trackSearch = useTrackSearch();
  const location = useLocation();
  const referrer = location.search ? `${location.pathname}${location.search}` : location.pathname;
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });

  const searchConfigs = useAutocompleteSearchConfigs();

  const updateUrl = (searchQuery: string) => {
    let updatedQuery = searchQuery;
    const newQueryParams: Record<string, string> = {};
    // partner matches should set a filter value instead of a query
    if (isExactPartnerNameMatch(searchQuery)) {
      // add partner filter
      newQueryParams.partners = searchQuery;
      // remove query text
      updatedQuery = '';
    }

    const cleanedQueryParams = omit(currentQueryParams, ['page', 'sortBy']);
    const searchUrl = getSearchUpdatedUrl(updatedQuery, { ...cleanedQueryParams, ...newQueryParams });
    if (isConsumerSearchPage) {
      const legacySearchUrl = getSearchUpdatedUrl(updatedQuery, newQueryParams);
      router.push(legacySearchUrl);
    } else {
      // inter-app routing does not currently work so resorting to global builtin
      window.location.assign(searchUrl);
    }
  };

  // fired on selection AND native form submit
  const handleAutocompleteSubmit = (rawQuery: string, mergedItemsForTracking?: AutocompleteItems) => {
    const sectionIdentifierPrefix = autocompletePrefixes.find((prefix) => rawQuery.startsWith(prefix));
    const queryValue = sectionIdentifierPrefix ? rawQuery.replace(sectionIdentifierPrefix, '') : rawQuery;

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING && onboardingUrl) {
      trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems);
      hideMobileSearchPage?.();
      window.location.assign(onboardingUrl);
      return;
    }

    // Check if navigation should be allowed for this query
    if (!shouldAllowSearchNavigation(queryValue, currentQueryParams)) {
      // Prevent navigation for organic empty searches
      return;
    }

    // Only track searches that will actually execute
    // Use mergedItemsForTracking if provided (includes recently viewed + trending products from zero state)
    trackSearch(rawQuery, autocompleteMode, mergedItemsForTracking || recentlyViewedItems);

    // Hide mobile search page since search is proceeding
    hideMobileSearchPage?.();

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED) {
      // Search in mergedItemsForTracking first (includes trending products), then fall back to recentlyViewedItems
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const recentlyViewedItemMatch = itemsToSearch.find((item) => item.name === queryValue);
      if (recentlyViewedItemMatch?.path) {
        window?.location.assign(recentlyViewedItemMatch.path);
        return;
      }
    }

    if (sectionIdentifierPrefix === AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES) {
      // For zero state trending products, search in mergedItemsForTracking first (includes merged trending products),
      // then fall back to recentlyViewedItems
      // This ensures we find trending products that were merged into LEARNER_FAVORITES or RECENTLY_VIEWED groups
      const itemsToSearch = mergedItemsForTracking || recentlyViewedItems;
      const directMatchItem = itemsToSearch.find((item) => item.name === queryValue || item.textValue === queryValue);
      if (directMatchItem?.path) {
        window?.location.assign(directMatchItem.path);
        return;
      }
    }

    // For all other selections (trending, as you type, etc.), save and navigate to search
    // save to local storage
    saveRecentlySearched(queryValue);
    // Refresh recently searched items to reflect the new search
    setRefreshKey((prev) => prev + 1);
    updateUrl(queryValue);
  };

  const handleNativeFormSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    handleAutocompleteSubmit(searchText);
  };

  const handleInputChange = (query: string) => {
    setSearchText(query);
    setAutocompleteMode(inferAutocompleteMode(query, recentlySearchedItems, recentlyViewedItems));
  };

  // ensure the search text is updated when the url changes (e.g., suggested search click on the SERP)
  React.useEffect(() => {
    setSearchText(currentQueryParams?.query ?? '');
  }, [currentQueryParams?.query]);

  // ensure the menu state is updated when the recently viewed data loads
  React.useEffect(() => {
    setAutocompleteMode(inferAutocompleteMode(searchText, recentlySearchedItems, recentlyViewedItems));
  }, [recentlySearchedItems, recentlyViewedItems, searchText]);

  const handleRefreshItems = React.useCallback<() => void>(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  return (
    <div className="rc-SearchBarContentWrapper" css={styles.wrapper}>
      <SearchProvider searchConfigs={searchConfigs} ssr={false} fetchPolicy="no-cache">
        <form className="search-form" role="search" onSubmit={handleNativeFormSubmit} css={styles.searchForm}>
          <SearchBarContent
            {...props}
            searchText={searchText}
            onSubmit={handleAutocompleteSubmit}
            autocompleteMode={autocompleteMode}
            onInputChange={handleInputChange}
            recentlySearchedItems={recentlySearchedItems}
            recentlyViewedItems={recentlyViewedItems}
            onRefreshItems={handleRefreshItems}
            searchIsOpen={searchIsOpen ?? false}
          />
        </form>
      </SearchProvider>
    </div>
  );
};

export default SearchBarContentWrapper;
